import { GetOrganizationsInOwnGeoAreaSelectionTagsEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { LegendListRef } from '@legendapp/list/react-native';
import { useBottomTabBarHeight } from '@react-navigation/bottom-tabs';
import { Activity, type FC, useEffect, useRef } from 'react';
import { StyleSheet, View } from 'react-native';

import { MeinOrtAlleTab } from '@/features/mein-ort/components/funkzentrale/MeinOrtAlleTab';
import { MeinOrtChatsTab } from '@/features/mein-ort/components/MeinOrtChatsTab';
import { MeinOrtGroupsTab } from '@/features/mein-ort/components/MeinOrtGroupsTab';
import { MeinOrtOrganizationsTab } from '@/features/mein-ort/components/MeinOrtOrganizationsTab';
import { useMeinOrtScroll } from '@/features/mein-ort/components/MeinOrtScrollContext';
import { useMeinOrtTab } from '@/features/mein-ort/components/MeinOrtTabContext';
import { MeinOrtTab } from '@/features/mein-ort/components/meinOrtTypes';
import {
  MeinOrtNewsFilterRow,
  NEWS_FILTER_HEIGHT
} from '@/features/mein-ort/components/news-filter/MeinOrtNewsFilterRow';
import { MeinOrtTerritorialAuthorityTab } from '@/features/mein-ort/components/territorial-authority/MeinOrtTerritorialAuthorityTab';
import {
  MeinOrtFeedKind,
  useMeinOrtPostFeed
} from '@/features/mein-ort/hooks/useMeinOrtPostFeed';
import { PostFeed } from '@/features/posts/components/PostFeed';
import { useBadgeStore } from '@/shared/store/useBadgeStore';

type OrganizationTabConfig = {
  tag: GetOrganizationsInOwnGeoAreaSelectionTagsEnum;
  emptyKey:
    | 'organizations.empty.vereine'
    | 'organizations.empty.ehrenamt'
    | 'organizations.empty.schulen'
    | 'organizations.empty.kirche';
};

const ORGANIZATION_TAB: Partial<Record<MeinOrtTab, OrganizationTabConfig>> = {
  [MeinOrtTab.VEREINE]: {
    tag: GetOrganizationsInOwnGeoAreaSelectionTagsEnum.Club,
    emptyKey: 'organizations.empty.vereine'
  },
  [MeinOrtTab.EHRENAMT]: {
    tag: GetOrganizationsInOwnGeoAreaSelectionTagsEnum.Volunteering,
    emptyKey: 'organizations.empty.ehrenamt'
  },
  [MeinOrtTab.SCHULEN]: {
    tag: GetOrganizationsInOwnGeoAreaSelectionTagsEnum.Education,
    emptyKey: 'organizations.empty.schulen'
  },
  [MeinOrtTab.KIRCHE]: {
    tag: GetOrganizationsInOwnGeoAreaSelectionTagsEnum.Religion,
    emptyKey: 'organizations.empty.kirche'
  }
};

const styles = StyleSheet.create({
  container: { flex: 1 }
});

export const MeinOrtBody: FC = () => {
  const { activeTab, setPostFeedRef } = useMeinOrtTab();
  const { scrollY, feedInset } = useMeinOrtScroll();
  const bottomTabBarHeight = useBottomTabBarHeight();
  const postFeedRef = useRef<LegendListRef>(null);

  const organizationTab = ORGANIZATION_TAB[activeTab];

  const registerPostFeedRef = (ref: LegendListRef | null) => {
    postFeedRef.current = ref;
    setPostFeedRef(ref);
  };

  const feed = useMeinOrtPostFeed(activeTab);

  const markPostsAsRead = useBadgeStore((state) => state.markPostsAsRead);
  useEffect(() => {
    if (feed.kind === MeinOrtFeedKind.NONE) return;
    const posts = feed.query.data;
    if (!posts?.length) return;
    markPostsAsRead(posts.map((p) => p.postId));
  }, [feed.kind, feed.query.data, markPostsAsRead]);

  const listContentStyle = {
    paddingTop: feedInset,
    paddingBottom: bottomTabBarHeight
  };
  const listStyle = [
    styles.container,
    {
      transform: [
        {
          translateY:
            feed.kind === MeinOrtFeedKind.NEWS ? NEWS_FILTER_HEIGHT : 0
        }
      ]
    }
  ];

  return (
    <>
      <Activity
        mode={feed.kind === MeinOrtFeedKind.NONE ? 'hidden' : 'visible'}
      >
        <View style={styles.container}>
          <PostFeed params={feed.params} query={feed.query}>
            <PostFeed.Loading />
            <PostFeed.Error />
            <View style={listStyle}>
              <PostFeed.List
                ref={registerPostFeedRef}
                scrollOffset={scrollY}
                contentContainerStyle={listContentStyle}
              />
            </View>
          </PostFeed>
          {feed.kind === MeinOrtFeedKind.NEWS && (
            <MeinOrtNewsFilterRow
              value={feed.newsFilter}
              onSelect={feed.setNewsFilter}
            />
          )}
        </View>
      </Activity>

      {activeTab === MeinOrtTab.ALLE && <MeinOrtAlleTab />}
      {activeTab === MeinOrtTab.GRUPPEN && <MeinOrtGroupsTab />}
      {activeTab === MeinOrtTab.CHATS && <MeinOrtChatsTab />}
      {activeTab === MeinOrtTab.TERRITORIAL_AUTHORITY && (
        <MeinOrtTerritorialAuthorityTab />
      )}
      {organizationTab && (
        <MeinOrtOrganizationsTab
          tag={organizationTab.tag}
          emptyKey={organizationTab.emptyKey}
        />
      )}
    </>
  );
};
